import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  GeoJSON,
  Popup,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* Inline styles for map label overlays */
const LABEL_STYLES = document.createElement("style");
LABEL_STYLES.textContent = `
  .mig-disp-city-label-tooltip {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    font-weight: 700;
    font-size: 13px;
    color: #1e3a8a;
    text-shadow: 0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff;
    white-space: nowrap;
  }
  .mig-disp-city-label-tooltip::before { display: none !important; }
  .mig-disp-brgy-label-icon span {
    font-size: 13px;
    font-weight: 700;
    color: #1d4ed8;
    text-shadow: 0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff;
    white-space: nowrap;
    pointer-events: none;
    letter-spacing: 0.03em;
  }
  .measure-tooltip {
    background: rgba(255,255,255,0.95) !important;
    border: 1px solid #f97316 !important;
    border-radius: 4px !important;
    padding: 2px 6px !important;
    box-shadow: 0 1px 4px rgba(0,0,0,0.15) !important;
    font-family: inherit;
    white-space: nowrap;
  }
  .measure-tooltip::before { border-right-color: #f97316 !important; }
`;
if (!document.querySelector("#mig-disp-dash-label-styles")) {
  LABEL_STYLES.id = "mig-disp-dash-label-styles";
  document.head.appendChild(LABEL_STYLES);
}

import api from "../api/client";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import DateFilter from "../components/dashboard/DateFilter";
import DeployedTeamsPanel from "../components/dashboard/DeployedTeamsPanel";
import NoTeamsPanel from "../components/dashboard/NoTeamsPanel";
import TeamAssignmentPopover from "../components/common/TeamAssignmentPopover";
import MapPanelTabs from "../components/dashboard/MapPanelTabs";
import { MODULE_THEMES } from "../utils/moduleTheme";
import { geocodeSubscriberAddress } from "../services/geocodingService";
import NapOverlay from "../components/map/NapOverlay";
import NapPulseMarker from "../components/map/NapPulseMarker";
import MeasureTool from "../components/map/MeasureTool";
import NapDetailsPanel from "../components/common/NapDetailsPanel";
import NapTableModal from "../components/modals/NapTableModal";
import CoordinateBackfillTool from "../components/modals/CoordinateBackfillTool";
import BARANGAY_COORDS, {
  SERVICE_AREA_CENTER,
  SERVICE_AREA_ZOOM,
  getBarangayCoords,
} from "../data/barangayGeodata";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MIG = MODULE_THEMES.migration;
const HEAD_OFFICE = [14.611065, 121.089339];
const HEAD_OFFICE_ICON = L.divIcon({
  className: "",
  html: `<div style="
    display:flex;align-items:center;justify-content:center;
    width:32px;height:32px;border-radius:50%;
    background:#1e40af;border:3px solid #fff;
    box-shadow:0 2px 8px rgba(0,0,0,0.3);
  "><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const STATUS_COLORS = {
  Pending: "#eab308",
  Completed: "#22c55e",
  Rescheduled: "#f97316",
  Cancelled: "#ef4444",
};

const STATUS_KEYS = [
  ["pending", "Pending"],
  ["completed", "Completed"],
  ["rescheduled", "Rescheduled"],
  ["cancelled", "Cancelled"],
];

// Team marker colors (cycled for multiple teams)
const TEAM_COLORS = [
  "#e11d48", "#7c3aed", "#0891b2", "#059669",
  "#d97706", "#dc2626", "#4f46e5", "#0d9488",
];

// Subscriber search pin — house icon matching NI map style
const SUBSCRIBER_SEARCH_ICON = L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
    <div style="
      display:flex;align-items:center;justify-content:center;
      width:60px;height:60px;border-radius:50%;
      background:#eab308;border:4px solid #fff;
      box-shadow:0 4px 14px rgba(234,179,8,0.6);
      animation:subscriber-pulse 2s ease-out infinite;
      --pulse-max-scale:1.4;
    "><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg></div>
    <div style="width:4px;height:12px;background:#dc2626;"></div>
    <div style="width:14px;height:14px;background:#dc2626;border-radius:50%;border:2px solid #fff;box-shadow:0 2px 6px rgba(220,38,38,0.7);"></div>
  </div>`,
  iconSize: [60, 86],
  iconAnchor: [30, 86],
  popupAnchor: [0, -86],
});

// Inject subscriber pulse animation if not already present
if (!document.querySelector("#mig-subscriber-pulse-style")) {
  const style = document.createElement("style");
  style.id = "mig-subscriber-pulse-style";
  style.textContent = `
    @keyframes subscriber-pulse {
      0% { box-shadow: 0 0 0 0 rgba(234,179,8,0.5); }
      70% { box-shadow: 0 0 0 20px rgba(234,179,8,0); }
      100% { box-shadow: 0 0 0 0 rgba(234,179,8,0); }
    }
  `;
  document.head.appendChild(style);
}

// FST location icon for desktop dashboard — person marker
function buildFSTIcon(isActive) {
  const bg = isActive ? "#3b82f6" : "#9ca3af";
  return L.divIcon({
    className: "",
    html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
      <div style="
        display:flex;align-items:center;justify-content:center;
        width:28px;height:28px;border-radius:50%;
        background:${bg};border:2.5px solid #fff;
        box-shadow:0 2px 6px rgba(0,0,0,0.35);
      "><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg></div>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getLocalToday() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || 15, { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

// Pans the map to a coordinate WITHOUT changing the current zoom level.
// Used when a job order is clicked from the Deployed Teams panel. `target`
// carries a `nonce` so repeated clicks on the same JO re-trigger the pan.
function PanTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target && target.lat != null && target.lng != null) {
      map.panTo([target.lat, target.lng]);
    }
  }, [target, map]);
  return null;
}

function ZoomWatcher({ onZoomChange }) {
  useMapEvents({
    zoomend(e) {
      onZoomChange(e.target.getZoom());
    },
  });
  return null;
}

function MapClickToPlace({ active, onPlaced }) {
  const enabledAtRef = useRef(0);
  useEffect(() => {
    if (active) enabledAtRef.current = Date.now();
  }, [active]);
  useMapEvents({
    click(e) {
      if (!active) return;
      if (Date.now() - enabledAtRef.current < 100) return;
      if (e.originalEvent?.target?.closest(".leaflet-popup, .leaflet-control")) return;
      onPlaced(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MigMapZoomTracker({ onZoomChange }) {
  const map = useMapEvents({
    zoomend() { onZoomChange(map.getZoom()); },
  });
  useEffect(() => { onZoomChange(map.getZoom()); }, [map, onZoomChange]);
  return null;
}

function BrgyLabels({ data }) {
  const map = useMap();
  useEffect(() => {
    if (!data || !map) return;
    const markers = [];
    (data.features || []).forEach((f) => {
      const name = f.properties?.brgy_name || "";
      if (!name) return;
      const centroid = L.geoJSON(f).getBounds().getCenter();
      const marker = L.marker(centroid, {
        icon: L.divIcon({
          className: "",
          html: `<span style="font-size:13px;font-weight:700;color:#1d4ed8;text-shadow:0 0 4px #fff,0 0 4px #fff,0 0 4px #fff;white-space:nowrap;pointer-events:none;letter-spacing:0.03em;">${name}</span>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
        interactive: false,
      });
      marker.addTo(map);
      markers.push(marker);
    });
    return () => markers.forEach((m) => map.removeLayer(m));
  }, [data, map]);
  return null;
}

function circleRadius(total) {
  if (total >= 300) return 18;
  if (total >= 100) return 14;
  if (total >= 50) return 11;
  if (total >= 20) return 8;
  if (total >= 5) return 6;
  return 5;
}

// ---------------------------------------------------------------------------
// Pie chart icon builder
// ---------------------------------------------------------------------------
const pieIconCache = {};

function buildPieIcon(brgy, size) {
  const vals = STATUS_KEYS.map(([k]) => brgy[k] || 0).join(",");
  const cacheKey = `migd-${brgy.municipality}-${brgy.barangay}-${size}-${vals}`;
  if (pieIconCache[cacheKey]) return pieIconCache[cacheKey];

  const slices = [];
  let total = 0;
  for (const [key, label] of STATUS_KEYS) {
    const val = brgy[key] || 0;
    if (val > 0) {
      slices.push({ label, value: val, color: STATUS_COLORS[label] });
      total += val;
    }
  }

  const r = size / 2;
  const cx = r;
  const cy = r;
  let svg;

  if (slices.length <= 1) {
    const color = slices.length === 1 ? slices[0].color : "#9ca3af";
    svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${cx}" cy="${cy}" r="${r - 1}" fill="${color}" fill-opacity="0.8" stroke="#fff" stroke-width="1.5"/>
    </svg>`;
  } else {
    let paths = "";
    let angle = -Math.PI / 2;
    for (const slice of slices) {
      const sliceAngle = (slice.value / total) * 2 * Math.PI;
      const x1 = cx + (r - 1) * Math.cos(angle);
      const y1 = cy + (r - 1) * Math.sin(angle);
      const x2 = cx + (r - 1) * Math.cos(angle + sliceAngle);
      const y2 = cy + (r - 1) * Math.sin(angle + sliceAngle);
      const largeArc = sliceAngle > Math.PI ? 1 : 0;
      paths += `<path d="M${cx},${cy} L${x1},${y1} A${r - 1},${r - 1} 0 ${largeArc} 1 ${x2},${y2} Z" fill="${slice.color}" fill-opacity="0.8"/>`;
      angle += sliceAngle;
    }
    svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      ${paths}
      <circle cx="${cx}" cy="${cy}" r="${r - 1}" fill="none" stroke="#fff" stroke-width="1.5"/>
    </svg>`;
  }

  const icon = L.divIcon({
    className: "",
    html: svg,
    iconSize: [size, size],
    iconAnchor: [r, r],
  });
  pieIconCache[cacheKey] = icon;
  return icon;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function MigrationDispatchDashboardPage() {
  const { user, canAccessMigrationDashboard, hasPerm, isMobileRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [filterMuni, setFilterMuni] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Sub-stat filters
  const [pendingTeamFilter, setPendingTeamFilter] = useState(null);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [cancelReasonFilter, setCancelReasonFilter] = useState(null);
  const [reschedReasonFilter, setReschedReasonFilter] = useState(null);

  // Date range
  const [dateFrom, setDateFrom] = useState(() => location.state?.dateFrom || getLocalToday());
  const [dateTo, setDateTo] = useState(() => location.state?.dateTo || getLocalToday());

  // Whether the selected date range starts on today (rescheduled only shown for today)
  const isToday = dateFrom === getLocalToday();

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const searchTimer = useRef(null);

  // Map focus
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(null);
  const [focusNapPos, setFocusNapPos] = useState(null); // pulsating highlight for a NAP opened from a detail modal
  // Pan target (keeps current zoom) — set when a job order is clicked in the
  // Deployed Teams panel. Separate from flyTarget so zoom is never changed.
  const [panTarget, setPanTarget] = useState(null);

  // Map records
  const [mapRecords, setMapRecords] = useState([]);

  // AbortController
  const mapAbortRef = useRef(null);
  const migMapRef = useRef(null);

  // Teams
  const [dispatchTeams, setDispatchTeams] = useState([]);
  const [teamsSummary, setTeamsSummary] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  // Deployed Teams panel: show only the user's own company by default.
  const [showOtherCompanyTeams, setShowOtherCompanyTeams] = useState(false);

  // Map layers
  const [showBasemap, setShowBasemap] = useState(true);
  const [showSatellite, setShowSatellite] = useState(false);
  const [showStreetLabels, setShowStreetLabels] = useState(true);
  const [showLandmarks, setShowLandmarks] = useState(false);
  const [showBrgyBoundaries, setShowBrgyBoundaries] = useState(false);
  const [showCityBoundaries, setShowCityBoundaries] = useState(true);
  const [showBrgyLabels, setShowBrgyLabels] = useState(true);
  const [showTeamLabels, setShowTeamLabels] = useState(true);
  const [showFSTLocations, setShowFSTLocations] = useState(false);
  const [fstLocations, setFstLocations] = useState([]);

  // Right-side tabbed panel (parity with NI Map)
  const [rightTab, setRightTab] = useState("noTeams");

  // Subscriber locate state (triggered from table name click)
  const [subscriberPin, setSubscriberPin] = useState(null);
  const [editingLocation, setEditingLocation] = useState(false);
  const [editLocConfirm, setEditLocConfirm] = useState(false);
  const [editLocSaving, setEditLocSaving] = useState(false);
  const [subscriberLocating, setSubscriberLocating] = useState(false);
  const [subscriberLocateMsg, setSubscriberLocateMsg] = useState(null);
  const pendingLocateRef = useRef(location.state?.locateSubscriber || null);
  const subscriberLocateExecuted = useRef(false);

  // NAP overlay state (same as NI map)
  const canViewNapOnMap = hasPerm("nap_locator.view_on_ni_map");
  const [napViewEnabled, setNapViewEnabled] = useState(canViewNapOnMap);
  const [migMapZoom, setMigMapZoom] = useState(SERVICE_AREA_ZOOM);
  const [selectedNap, setSelectedNap] = useState(null);
  const [napTableModalSearch, setNapTableModalSearch] = useState(null);

  // Measure tool state
  const [measureEnabled, setMeasureEnabled] = useState(false);
  const [measureTotal, setMeasureTotal] = useState(0);

  const [brgyGeoJSON, setBrgyGeoJSON] = useState(null);
  const [cityGeoJSON, setCityGeoJSON] = useState(null);

  // Build centroid lookup from GeoJSON polygons
  const geoCentroids = useMemo(() => {
    const lookup = {};
    if (!brgyGeoJSON) return lookup;
    const CITY_TO_MUNI = {
      "city of pasig": "PASIG",
      "city of mandaluyong": "MANDALUYONG",
      "city of marikina": "MARIKINA",
      "city of san juan": "SAN JUAN",
    };
    const norm = (s) =>
      s.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
       .toUpperCase()
       .replace(/\(.*?\)/g, "")
       .replace(/[.\-]/g, " ")
       .replace(/\s+/g, " ")
       .trim();

    for (const f of brgyGeoJSON.features || []) {
      const rawBrgy = f.properties?.brgy_name || "";
      const city = (f.properties?.city_name || "").toLowerCase();
      const muni = CITY_TO_MUNI[city];
      if (!muni || !rawBrgy) continue;
      const center = L.geoJSON(f).getBounds().getCenter();
      const coords = [center.lat, center.lng];
      lookup[`${muni}|${rawBrgy.toUpperCase()}`] = coords;
      lookup[`${muni}|${norm(rawBrgy)}`] = coords;
    }

    const ALIASES = {
      "MARIKINA|STA ELENA": "MARIKINA|SANTA ELENA",
      "MARIKINA|STO NINO": "MARIKINA|SANTO NINO",
      "MARIKINA|CONCEPCION 2": "MARIKINA|CONCEPCION DOS",
      "MARIKINA|JESUS DELA PENA": "MARIKINA|JESUS DE LA PENA",
      "MARIKINA|PARAISO": "MARIKINA|FORTUNE",
      "PASIG|STA CRUZ": "PASIG|SANTA CRUZ",
      "PASIG|STA LUCIA": "PASIG|SANTA LUCIA",
      "PASIG|STO TOMAS": "PASIG|SANTO TOMAS",
      "PASIG|SAN NICOLAS": "PASIG|SAN NICOLAS",
      "PASIG|KATIPUNAN": "PASIG|BAGONG KATIPUNAN",
      "PASIG|NAPICO": "PASIG|PINAGBUHATAN",
      "PASIG|UGONG NORTE": "PASIG|UGONG",
      "MANDALUYONG|NEW ZANIGA": "MANDALUYONG|NEW ZANIGA",
      "MANDALUYONG|OLD ZANIGA": "MANDALUYONG|OLD ZANIGA",
      "MANDALUYONG|PAG ASA": "MANDALUYONG|PAG ASA",
      "MANDALUYONG|MABINI J RIZAL": "MANDALUYONG|MABINI J RIZAL",
      "MANDALUYONG|WACK WACK": "MANDALUYONG|WACK WACK GREENHILLS",
      "MANDALUYONG|WACK WACK GREENHILLS": "MANDALUYONG|WACK WACK GREENHILLS",
      "MANDALUYONG|HAGDANG BATO": "MANDALUYONG|HAGDANG BATO ITAAS",
      "MANDALUYONG|GREENHILLS": "MANDALUYONG|WACK WACK GREENHILLS",
      "SAN JUAN|STA LUCIA": "SAN JUAN|SANTA LUCIA",
      "SAN JUAN|BALONG BATO": "SAN JUAN|BALONG BATO",
      "SAN JUAN|ERMITANO": "SAN JUAN|ERMITANO",
      "SAN JUAN|PASADENA": "SAN JUAN|PASADENA",
      "SAN JUAN|SAN JOSE": "SAN JUAN|SAN PERFECTO",
    };

    for (const [dbKey, geoKey] of Object.entries(ALIASES)) {
      const normGeo = norm(geoKey.split("|")[1]);
      const muni = geoKey.split("|")[0];
      const resolved = lookup[`${muni}|${normGeo}`] || lookup[geoKey];
      if (resolved) lookup[dbKey] = resolved;
    }

    return lookup;
  }, [brgyGeoJSON]);

  // Resolve coords
  const resolveCoords = useCallback(
    (municipality, barangay) => {
      const muni = (municipality || "").toUpperCase();
      const brgy = (barangay || "").toUpperCase();
      const key = `${muni}|${brgy}`;
      if (geoCentroids[key]) return geoCentroids[key];
      const normKey = `${muni}|${brgy.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\(.*?\)/g, "").replace(/[.\-]/g, " ").replace(/\s+/g, " ").trim()}`;
      if (geoCentroids[normKey]) return geoCentroids[normKey];
      return getBarangayCoords(municipality, barangay);
    },
    [geoCentroids]
  );

  // Load boundary GeoJSON files
  useEffect(() => {
    fetch("/data/barangay_boundaries.geojson")
      .then((r) => r.json())
      .then(setBrgyGeoJSON)
      .catch(() => {});
    fetch("/data/city_boundaries.geojson")
      .then((r) => r.json())
      .then(setCityGeoJSON)
      .catch(() => {});
  }, []);

  // Poll FST live locations every 15 seconds
  useEffect(() => {
    if (isMobileRole) return;
    let cancelled = false;
    const fetchLocations = () => {
      api.get("/fst/locations")
        .then((r) => { if (!cancelled) setFstLocations(r.data); })
        .catch(() => {});
    };
    fetchLocations();
    const timer = setInterval(fetchLocations, 15_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [isMobileRole]);

  // Redirect if no permission
  useEffect(() => {
    if (!canAccessMigrationDashboard) navigate("/dashboard", { replace: true });
  }, [canAccessMigrationDashboard, navigate]);

  // Fetch data
  //
  // Resilience notes — fixes the erratic "Failed to load migration dispatch
  // dashboard data." on the map view. The backend always answers 200; the
  // failures were entirely client-side:
  //   * The API origin is connection-saturated on this page — a persistent SSE
  //     stream (/notifications/stream) permanently holds one of the browser's
  //     ~6 HTTP/1.1 sockets, and several pollers (fst/locations, notifications,
  //     me, preferences) compete for the rest.
  //   * Under React 18 StrictMode's dev double-mount (and on any date change),
  //     this effect runs again and the shared AbortController aborts the first
  //     batch mid-flight.
  //   * With the old Promise.all + broad catch, a single cancelled/reset
  //     sibling blanked the whole dashboard (stats → 0), and a genuine reset
  //     surfaced the error banner even though nothing was actually wrong.
  // The fetch is now race-safe: results from a superseded/aborted run are
  // dropped, each request is applied independently (allSettled), the error
  // banner is only raised when the critical map-records call genuinely fails,
  // and map-records is retried once on a transient network error.
  const fetchData = useCallback(async () => {
    if (mapAbortRef.current) mapAbortRef.current.abort();
    const controller = new AbortController();
    mapAbortRef.current = controller;

    // A run is "dead" once its own signal is aborted (superseded by a newer
    // run, or the component unmounted) — never apply its results to state.
    const isDead = () => controller.signal.aborted || mapAbortRef.current !== controller;
    const isCancel = (e) =>
      e?.name === "CanceledError" || e?.code === "ERR_CANCELED" || controller.signal.aborted;

    setLoading(true);
    setError("");

    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (filterMuni) params.municipality = filterMuni;

    // map-records fetched without municipality filter for consistency
    const dateParams = {};
    if (dateFrom) dateParams.date_from = dateFrom;
    if (dateTo) dateParams.date_to = dateTo;

    // Retry the critical map-records call once on a transient (non-cancel)
    // failure — the API origin is often connection-saturated on this page.
    const getMapRecords = async () => {
      try {
        return await api.get("/migration/dispatch-dashboard/map-records", { params: dateParams, signal: controller.signal });
      } catch (e) {
        if (isCancel(e) || isDead()) throw e;
        return api.get("/migration/dispatch-dashboard/map-records", { params: dateParams, signal: controller.signal });
      }
    };

    const [teamsRes, teamsSumRes, mapRes] = await Promise.allSettled([
      api.get("/migration/dispatch-dashboard/dispatch-teams-today", { params, signal: controller.signal }),
      api.get("/migration/dispatch-dashboard/dispatch-teams-today-summary", { params, signal: controller.signal }),
      getMapRecords(),
    ]);

    // A newer run (date change / StrictMode remount / unmount) took over —
    // bail out so we never clobber the current run's data or flip loading.
    if (isDead()) return;

    if (teamsRes.status === "fulfilled") setDispatchTeams(teamsRes.value.data);
    if (teamsSumRes.status === "fulfilled") setTeamsSummary(teamsSumRes.value.data);

    if (mapRes.status === "fulfilled") {
      setMapRecords(mapRes.value.data);
      setSelectedTeam(null);
      setError("");
    } else if (!isCancel(mapRes.reason)) {
      setError("Failed to load migration dispatch dashboard data.");
      setMapRecords([]);
    }

    setLoading(false);
  }, [dateFrom, dateTo, filterMuni]);

  useEffect(() => {
    fetchData();
    // Abort in-flight requests when the effect re-runs or the page unmounts.
    return () => {
      if (mapAbortRef.current) mapAbortRef.current.abort();
    };
  }, [fetchData]);

  // Process pending subscriber location request (from Migration Table name click)
  useEffect(() => {
    if (loading) return;
    if (subscriberLocateExecuted.current) return;
    const req = pendingLocateRef.current;
    if (!req) return;
    subscriberLocateExecuted.current = true;
    pendingLocateRef.current = null;

    // Phase 1: Instant fly using existing coords or local barangay centroid
    const hasCoords = req.latitude && req.longitude && !isNaN(req.latitude) && !isNaN(req.longitude);
    let immediateCoords = null;

    if (hasCoords) {
      immediateCoords = [parseFloat(req.latitude), parseFloat(req.longitude)];
    } else {
      immediateCoords = getBarangayCoords(req.municipality, req.barangay);
    }

    if (immediateCoords) {
      const pin = {
        id: req.id || null,
        lat: immediateCoords[0],
        lng: immediateCoords[1],
        name: req.customerName,
        address: req.address,
        municipality: req.municipality,
        barangay: req.barangay,
        oldacctno: req.oldacctno,
      };
      setSubscriberPin(pin);
      setFlyTarget(immediateCoords);
      setFlyZoom(hasCoords ? 18 : 16);
      setEditingLocation(true);
      setEditLocConfirm(false);
      if (hasCoords && req.locationSaved) {
        setSubscriberLocateMsg({ type: "success", text: "Subscriber location found." });
        return; // saved location — no need for geocoding
      }
      if (hasCoords) {
        setSubscriberLocateMsg({ type: "success", text: "Subscriber location found." });
        return;
      }
      setSubscriberLocating(true);
      setSubscriberLocateMsg({ type: "warning", text: "Showing barangay area. Searching for exact address…" });
    } else {
      setSubscriberLocating(true);
      setSubscriberLocateMsg(null);
    }

    // Phase 2: Call geocoding API for street-level precision (background)
    const refineLocation = async () => {
      try {
        // Build address string including village/landmark for more accuracy
        let fullAddress = req.address || "";
        if (req.village && !fullAddress.toUpperCase().includes(req.village.toUpperCase())) {
          fullAddress = `${fullAddress} ${req.village}`.trim();
        }

        const result = await geocodeSubscriberAddress({
          address: fullAddress,
          barangay: req.barangay,
          municipality: req.municipality,
          latitude: null,
          longitude: null,
        });

        if (result.success && result.latitude && result.longitude) {
          const pin = {
            id: req.id || null,
            lat: result.latitude,
            lng: result.longitude,
            name: req.customerName,
            address: req.address,
            municipality: req.municipality,
            barangay: req.barangay,
            oldacctno: req.oldacctno,
          };
          setSubscriberPin(pin);
          setFlyTarget([result.latitude, result.longitude]);
          setFlyZoom(18);
          setEditingLocation(true);
          setEditLocConfirm(false);
          const msgType = (result.accuracy === "exact" || result.accuracy === "address") ? "success" : "warning";
          setSubscriberLocateMsg({ type: msgType, text: result.message });
        } else if (!immediateCoords) {
          setSubscriberLocateMsg({ type: "error", text: result.message });
        } else {
          setEditingLocation(true);
          setEditLocConfirm(false);
          setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
        }
      } catch {
        if (!immediateCoords) {
          setSubscriberLocateMsg({ type: "error", text: "Unable to locate subscriber address." });
        } else {
          setEditingLocation(true);
          setEditLocConfirm(false);
          setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
        }
      } finally {
        setSubscriberLocating(false);
      }
    };

    refineLocation();
  }, [loading]);

  // Focus a specific NAP when navigated from a Job Order / Migration detail modal:
  // turn on the NAP overlay and fly to the NAP location (zoom 18).
  const pendingFocusNapRef = useRef(location.state?.focusNap || null);
  const focusNapExecuted = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (focusNapExecuted.current) return;
    const f = pendingFocusNapRef.current;
    if (!f) return;
    focusNapExecuted.current = true;
    pendingFocusNapRef.current = null;
    const lat = parseFloat(f.latitude), lng = parseFloat(f.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    if (canViewNapOnMap) setNapViewEnabled(true);
    setFocusNapPos([lat, lng]);
    setFlyTarget([lat, lng]);
    setFlyZoom(18);
  }, [loading]);

  // Auto-dismiss subscriber locate message after 5 seconds
  useEffect(() => {
    if (!subscriberLocateMsg) return;
    const t = setTimeout(() => setSubscriberLocateMsg(null), 5000);
    return () => clearTimeout(t);
  }, [subscriberLocateMsg]);

  // Debounced search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = searchTerm.trim();
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/migration/dispatch-dashboard/search", {
          params: { q: term },
        });
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  // Pan the map to a record's location WITHOUT changing the current zoom.
  // Prefers exact coords, falls back to the barangay centroid, else a toast.
  const panToRecord = useCallback((rec) => {
    const hasCoords =
      rec.latitude != null && rec.longitude != null &&
      !isNaN(rec.latitude) && !isNaN(rec.longitude);

    if (hasCoords) {
      setPanTarget({ lat: parseFloat(rec.latitude), lng: parseFloat(rec.longitude), nonce: Date.now() });
    } else {
      const coords = resolveCoords(rec.municipality, rec.barangay);
      if (coords) {
        setPanTarget({ lat: coords[0], lng: coords[1], nonce: Date.now() });
        setSubscriberLocateMsg({ type: "warning", text: "No exact coordinates — showing barangay area." });
      } else {
        setSubscriberLocateMsg({ type: "error", text: "No valid coordinates for this job order." });
      }
    }
  }, [resolveCoords]);

  // Job order clicked from the Deployed Teams panel.
  // Reuses the same selection logic as a marker click (set record + open the
  // Subscriber tab) and pans the map to the location WITHOUT changing zoom.
  const handleJobOrderClick = useCallback((rec) => {
    setSelectedRecord(rec);
    setSelectedNap(null);
    setRightTab("subscriber");
    panToRecord(rec);
  }, [panToRecord]);

  // Card clicked from the No Teams tab — highlight + pan, but stay on the tab.
  const handleNoTeamCardClick = useCallback((rec) => {
    setSelectedRecord(rec);
    setSelectedNap(null);
    panToRecord(rec);
  }, [panToRecord]);

  // Inline team assignment (parity with the Migration table view)
  const [teamMenu, setTeamMenu] = useState(null); // { record, rect }
  const canEditMigTeam =
    hasPerm("migration.edit") &&
    hasPerm("mig.field.edit.team_number") &&
    hasPerm("mig.field.edit.personnel");

  const handleMigTeamClick = useCallback((rec, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTeamMenu({ record: rec, rect });
  }, []);

  const handleMigTeamSave = useCallback(() => {
    setTeamMenu(null);
    fetchData(); // refetch so the assigned record leaves the No Teams list
  }, [fetchData]);

  // Select a search result — locate subscriber on map with geocoding
  const handleSelectResult = useCallback(async (rec) => {
    setSelectedRecord(rec);
    setRightTab("subscriber");
    setSearchResults([]);
    setSearchTerm(rec.oldacctno || rec.name || "");

    const hasCoords = rec.latitude && rec.longitude && !isNaN(rec.latitude) && !isNaN(rec.longitude);

    // If location was previously saved, fly directly — no geocoding needed
    if (rec.location_saved && hasCoords) {
      const pin = {
        id: rec.id,
        lat: parseFloat(rec.latitude),
        lng: parseFloat(rec.longitude),
        name: rec.name,
        address: rec.address,
        municipality: rec.municipality,
        barangay: rec.barangay,
        oldacctno: rec.oldacctno,
      };
      setSubscriberPin(pin);
      setFlyTarget([pin.lat, pin.lng]);
      setFlyZoom(18);
      setEditingLocation(true);
      setEditLocConfirm(false);
      setSubscriberLocateMsg({ type: "success", text: "Subscriber location found." });
      return;
    }

    // No saved location — use coords or centroid, then geocode
    let immediateCoords = null;

    if (hasCoords) {
      immediateCoords = [parseFloat(rec.latitude), parseFloat(rec.longitude)];
    } else {
      immediateCoords = resolveCoords(rec.municipality, rec.barangay);
    }

    const pin = {
      id: rec.id,
      lat: immediateCoords ? immediateCoords[0] : 0,
      lng: immediateCoords ? immediateCoords[1] : 0,
      name: rec.name,
      address: rec.address,
      municipality: rec.municipality,
      barangay: rec.barangay,
      oldacctno: rec.oldacctno,
    };

    if (immediateCoords) {
      setSubscriberPin(pin);
      setFlyTarget([...immediateCoords]);
      setFlyZoom(hasCoords ? 18 : 16);
      setEditingLocation(true);
      setEditLocConfirm(false);
      if (hasCoords) {
        setSubscriberLocateMsg({ type: "success", text: "Subscriber location found." });
        return;
      }
    }

    // Phase 2: Geocode for precision
    setSubscriberLocating(true);
    setSubscriberLocateMsg(immediateCoords ? { type: "warning", text: "Showing barangay area. Searching for exact address…" } : null);

    try {
      let fullAddress = rec.address || "";
      if (rec.village && !fullAddress.toUpperCase().includes(rec.village.toUpperCase())) {
        fullAddress = `${fullAddress} ${rec.village}`.trim();
      }
      const result = await geocodeSubscriberAddress({
        address: fullAddress,
        barangay: rec.barangay,
        municipality: rec.municipality,
        latitude: null,
        longitude: null,
      });
      if (result.success && result.latitude && result.longitude) {
        setSubscriberPin({ ...pin, lat: result.latitude, lng: result.longitude });
        setFlyTarget([result.latitude, result.longitude]);
        setFlyZoom(18);
        setEditingLocation(true);
        setEditLocConfirm(false);
        const msgType = (result.accuracy === "exact" || result.accuracy === "address") ? "success" : "warning";
        setSubscriberLocateMsg({ type: msgType, text: result.message });
      } else if (immediateCoords) {
        setEditingLocation(true);
        setEditLocConfirm(false);
        setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
      } else {
        setSubscriberLocateMsg({ type: "error", text: result.message || "Unable to locate address." });
      }
    } catch {
      if (immediateCoords) {
        setEditingLocation(true);
        setEditLocConfirm(false);
        setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
      } else {
        setSubscriberLocateMsg({ type: "error", text: "Unable to locate subscriber address." });
      }
    } finally {
      setSubscriberLocating(false);
    }
  }, [resolveCoords]);

  // Locate subscriber by address — triggered from popup name click on house marker
  const handleLocateByAddress = useCallback(async (record) => {
    if (!record) return;
    const address = record.address || "";

    const hasCoords = record.latitude && record.longitude && !isNaN(record.latitude) && !isNaN(record.longitude);

    // If we already have a recorded lat/lon (saved or not), fly straight to it —
    // no geocoding. Mirrors the Table View: always land on the last recorded
    // location and let the user update the pin from there.
    if (hasCoords) {
      const pin = {
        id: record.id || null,
        lat: parseFloat(record.latitude),
        lng: parseFloat(record.longitude),
        name: record.name,
        address: address,
        municipality: record.municipality,
        barangay: record.barangay,
        oldacctno: record.oldacctno,
      };
      setSubscriberPin(pin);
      setEditingLocation(true);
      setEditLocConfirm(false);
      setFlyTarget([pin.lat, pin.lng]);
      setFlyZoom(18);
      setSubscriberLocateMsg({ type: "success", text: "Subscriber location found." });
      return;
    }

    // No recorded coords — Phase 1: instant fly to barangay centroid if available
    const immediateCoords = resolveCoords(record.municipality, record.barangay);

    const pinBase = {
      id: record.id || null,
      name: record.name,
      address: address,
      municipality: record.municipality,
      barangay: record.barangay,
      oldacctno: record.oldacctno,
    };

    if (immediateCoords) {
      setSubscriberPin({ ...pinBase, lat: immediateCoords[0], lng: immediateCoords[1] });
      setFlyTarget(immediateCoords);
      setFlyZoom(16);
    }

    setSubscriberLocating(true);
    setSubscriberLocateMsg({ type: "warning", text: "Searching for address-based location…" });

    // Phase 2: Geocode the address for precise location
    try {
      let fullAddress = address;
      if (record.village && !fullAddress.toUpperCase().includes(record.village.toUpperCase())) {
        fullAddress = `${fullAddress} ${record.village}`.trim();
      }
      const result = await geocodeSubscriberAddress({
        address: fullAddress,
        barangay: record.barangay,
        municipality: record.municipality,
        latitude: null,
        longitude: null,
      });

      if (result.success && result.latitude && result.longitude) {
        setSubscriberPin({ ...pinBase, lat: result.latitude, lng: result.longitude });
        setFlyTarget([result.latitude, result.longitude]);
        setFlyZoom(18);
        setEditingLocation(true);
        setEditLocConfirm(false);
        const msgType = (result.accuracy === "exact" || result.accuracy === "address") ? "success" : "warning";
        setSubscriberLocateMsg({ type: msgType, text: result.message });
      } else if (immediateCoords) {
        setEditingLocation(true);
        setEditLocConfirm(false);
        setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
      } else {
        setSubscriberLocateMsg({ type: "error", text: "Unable to locate subscriber address." });
      }
    } catch {
      if (immediateCoords) {
        setEditingLocation(true);
        setEditLocConfirm(false);
        setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
      } else {
        setSubscriberLocateMsg({ type: "error", text: "Unable to locate subscriber address." });
      }
    } finally {
      setSubscriberLocating(false);
    }
  }, [resolveCoords]);

  // Subscriber/customer NAME clicked from a panel card (Deployed Teams / No Teams).
  // Mirrors the table & map-marker name click: select the record and run the
  // locate-by-address flow (fly to saved flag, or geocode + drop an editable pin
  // with all the relocate/save controls).
  const handleSubscriberNameClick = useCallback((record) => {
    setSelectedRecord(record);
    setSelectedNap(null);
    handleLocateByAddress(record);
  }, [handleLocateByAddress]);

  // Navigate date
  const navigateDate = (days) => {
    const base = dateFrom ? new Date(dateFrom + "T00:00:00") : new Date();
    base.setDate(base.getDate() + days);
    const y = base.getFullYear();
    const m = String(base.getMonth() + 1).padStart(2, "0");
    const d = String(base.getDate()).padStart(2, "0");
    const newDate = `${y}-${m}-${d}`;
    setDateFrom(newDate);
    setDateTo(newDate);
  };

  const setToday = () => {
    const today = getLocalToday();
    setDateFrom(today);
    setDateTo(today);
  };

  // Build team color map
  const teamColorMap = useMemo(() => {
    const map = {};
    const allTeams = new Set();
    dispatchTeams.forEach((loc) => loc.teams.forEach((t) => allTeams.add(t.team_number)));
    teamsSummary.forEach((t) => allTeams.add(t.team_number));
    [...allTeams].sort().forEach((team, i) => {
      map[team] = TEAM_COLORS[i % TEAM_COLORS.length];
    });
    return map;
  }, [dispatchTeams, teamsSummary]);

  // Filtered dispatch markers based on selected team
  const filteredDispatchTeams = useMemo(() => {
    if (!selectedTeam) return dispatchTeams;
    return dispatchTeams
      .map((loc) => ({
        ...loc,
        teams: loc.teams.filter((t) => t.team_number === selectedTeam),
      }))
      .filter((loc) => loc.teams.length > 0);
  }, [dispatchTeams, selectedTeam]);

  const selectedTeamBrgys = useMemo(() => {
    if (!selectedTeam) return null;
    const s = new Set();
    dispatchTeams.forEach((loc) => {
      if (loc.teams.some((t) => t.team_number === selectedTeam)) {
        s.add(`${(loc.municipality || "").toUpperCase()}|${(loc.barangay || "").toUpperCase()}`);
      }
    });
    return s;
  }, [selectedTeam, dispatchTeams]);

  // Helper: team presence check
  const _hasTeam = useCallback((tn) => tn && tn.trim() && tn.trim() !== "-", []);
  const _isBest = useCallback((tn) => { const u = (tn || "").trim().toUpperCase(); return u.startsWith("BEST") || u.startsWith("BST"); }, []);
  const _isA6 = useCallback((tn) => { const u = (tn || "").trim().toUpperCase(); return u.startsWith("ATS") || u.startsWith("A6"); }, []);

  // Reset sub-filters
  const resetSubFilters = useCallback(() => {
    setPendingTeamFilter(null);
    setCompanyFilter(null);
    setCancelReasonFilter(null);
    setReschedReasonFilter(null);
  }, []);

  // Date change handler for DateFilter component (desktop, matches Migration Table behavior)
  const handleDateChange = useCallback((val) => {
    setDateFrom(val.from || "");
    setDateTo(val.to || "");
    // View All resets status filters (same as original behavior)
    if (!val.from && !val.to) {
      setFilterStatus("");
      setPendingTeamFilter(null);
      setCompanyFilter(null);
      setCancelReasonFilter(null);
      setReschedReasonFilter(null);
    }
  }, []);

  // Teams count — derived from teamsSummary (matches Migration Table top bar)
  const teamsCount = useMemo(() => teamsSummary.length, [teamsSummary]);

  // Auto-clear rescheduled filter when navigating away from today
  useEffect(() => {
    if (!isToday && filterStatus === "Rescheduled") {
      setFilterStatus("");
      setReschedReasonFilter(null);
    }
  }, [isToday, filterStatus]);

  // Filter individual map records
  const filteredMapRecords = useMemo(() => {
    let data = mapRecords;
    // Hide Rescheduled from map view when selected date is not today
    if (!isToday) {
      data = data.filter((r) => r.jo_status !== "Rescheduled");
    }
    if (filterMuni) {
      data = data.filter(
        (r) => (r.municipality || "").toUpperCase() === filterMuni.toUpperCase()
      );
    }
    if (filterStatus) {
      data = data.filter((r) => r.jo_status === filterStatus);
    }
    // Sub-stat filters
    if (filterStatus === "Pending" && pendingTeamFilter) {
      data = data.filter((r) =>
        pendingTeamFilter === "with_team" ? _hasTeam(r.team_number) : !_hasTeam(r.team_number)
      );
    }
    if (filterStatus === "Completed" && companyFilter) {
      data = data.filter((r) => {
        if (companyFilter === "best") return _isBest(r.team_number);
        if (companyFilter === "a6") return _isA6(r.team_number);
        if (companyFilter === "unknown") return !_isBest(r.team_number) && !_isA6(r.team_number);
        return true;
      });
    }
    if (filterStatus === "Cancelled" && cancelReasonFilter) {
      data = data.filter((r) => (r.cancel_reasons || []).includes(cancelReasonFilter));
    }
    if (filterStatus === "Rescheduled" && reschedReasonFilter) {
      data = data.filter((r) => (r.resched_reasons || []).includes(reschedReasonFilter));
    }
    if (selectedTeam) {
      data = data.filter((r) => r.team_number === selectedTeam);
    }
    return data;
  }, [mapRecords, filterMuni, filterStatus, selectedTeam, pendingTeamFilter, companyFilter, cancelReasonFilter, reschedReasonFilter, _hasTeam, _isBest, _isA6, isToday]);

  // Base-filtered records used for legend & stat counts
  // Hide Rescheduled when not viewing today
  const baseFilteredRecords = useMemo(() => {
    let data = [...mapRecords];
    if (!isToday) {
      data = data.filter((r) => r.jo_status !== "Rescheduled");
    }
    if (filterMuni) {
      data = data.filter(
        (r) => (r.municipality || "").toUpperCase() === filterMuni.toUpperCase()
      );
    }
    if (selectedTeam) {
      data = data.filter((r) => r.team_number === selectedTeam);
    }
    return data;
  }, [mapRecords, filterMuni, selectedTeam, isToday]);

  // Panel-only feed: same filters as baseFilteredRecords but intentionally KEEPS
  // Rescheduled history on past dates so each team card can reconstruct that
  // day's P (auto-rolled Pending) and R (manual reschedule) — the live records
  // only survive as C and X once a date is past. Map markers + legend keep using
  // baseFilteredRecords, so their past-date behavior is unchanged.
  const panelBaseRecords = useMemo(() => {
    let data = [...mapRecords];
    if (filterMuni) {
      data = data.filter(
        (r) => (r.municipality || "").toUpperCase() === filterMuni.toUpperCase()
      );
    }
    if (selectedTeam) {
      data = data.filter((r) => r.team_number === selectedTeam);
    }
    return data;
  }, [mapRecords, filterMuni, selectedTeam]);

  // Records scoped to the user's company for the Deployed Teams panel.
  // Migration records carry no company id, so the company is inferred from the
  // team-number prefix (BEST* vs ATS*/A6*) compared to the user's company.
  // Teams with an unrecognised prefix are always shown (never hidden).
  const companyScopedRecords = useMemo(() => {
    if (showOtherCompanyTeams) return panelBaseRecords;
    const cn = (user?.company_name || "").toUpperCase();
    const userIsBest = cn.includes("BEST") || cn.includes("BST");
    const userIsA6 = cn.includes("A6") || cn.includes("ATS");
    if (!userIsBest && !userIsA6) return panelBaseRecords; // unknown user company — show all
    return panelBaseRecords.filter((r) => {
      const best = _isBest(r.team_number);
      const a6 = _isA6(r.team_number);
      if (!best && !a6) return true; // unknown team prefix — don't hide
      return userIsBest ? best : a6;
    });
  }, [panelBaseRecords, showOtherCompanyTeams, user?.company_name, _isBest, _isA6]);

  // Records with no team assigned yet — source for the "No Teams" tab.
  const noTeamRecords = useMemo(() => {
    let data = mapRecords.filter((r) => !_hasTeam(r.team_number));
    if (filterMuni) {
      data = data.filter((r) => (r.municipality || "").toUpperCase() === filterMuni.toUpperCase());
    }
    return data.sort((a, b) =>
      `${a.barangay || ""}${a.name || ""}`.localeCompare(`${b.barangay || ""}${b.name || ""}`)
    );
  }, [mapRecords, filterMuni, _hasTeam]);

  // Legend counts — use jo_status to match the dispatch table
  const legendCounts = useMemo(() => {
    const counts = {};
    for (const r of baseFilteredRecords) {
      const s = r.jo_status;
      if (s === "Pending") counts.Pending = (counts.Pending || 0) + 1;
      else if (s === "Completed") counts.Completed = (counts.Completed || 0) + 1;
      else if (s === "Rescheduled") counts.Rescheduled = (counts.Rescheduled || 0) + 1;
      else if (s === "Cancelled") counts.Cancelled = (counts.Cancelled || 0) + 1;
    }
    return counts;
  }, [baseFilteredRecords]);

  // Sub-stat counts — use jo_status to match the dispatch table
  const mapSubStats = useMemo(() => {
    const pending = baseFilteredRecords.filter((r) => r.jo_status === "Pending");
    const completed = baseFilteredRecords.filter((r) => r.jo_status === "Completed");
    const cancelled = baseFilteredRecords.filter((r) => r.jo_status === "Cancelled");
    const rescheduled = baseFilteredRecords.filter((r) => r.jo_status === "Rescheduled");

    // Pending breakdown
    const pendingWithTeam = pending.filter((r) => _hasTeam(r.team_number)).length;
    const pendingWithoutTeam = pending.length - pendingWithTeam;

    // Completed company breakdown
    const compBest = completed.filter((r) => _isBest(r.team_number)).length;
    const compA6 = completed.filter((r) => _isA6(r.team_number)).length;
    const compUnknown = completed.length - compBest - compA6;

    // Cancel reason breakdown
    const cancelReasonCounts = {};
    for (const r of cancelled) {
      for (const reason of (r.cancel_reasons || [])) {
        cancelReasonCounts[reason] = (cancelReasonCounts[reason] || 0) + 1;
      }
    }

    // Resched reason breakdown
    const reschedReasonCounts = {};
    for (const r of rescheduled) {
      for (const reason of (r.resched_reasons || [])) {
        reschedReasonCounts[reason] = (reschedReasonCounts[reason] || 0) + 1;
      }
    }

    return {
      pendingWithTeam, pendingWithoutTeam,
      compBest, compA6, compUnknown,
      cancelReasonCounts, reschedReasonCounts,
    };
  }, [baseFilteredRecords, _hasTeam, _isBest, _isA6]);

  // Per-barangay stats from filteredMapRecords
  const filteredBrgyStats = useMemo(() => {
    const map = {};
    for (const r of filteredMapRecords) {
      const key = `${(r.municipality || "").toUpperCase()}|${(r.barangay || "").toUpperCase()}`;
      if (!map[key]) map[key] = { municipality: (r.municipality || "").toUpperCase(), barangay: (r.barangay || "").toUpperCase(), total: 0, pending: 0, completed: 0, cancelled: 0, rescheduled: 0 };
      map[key].total++;
      if (r.jo_status === "Pending") map[key].pending++;
      else if (r.jo_status === "Completed") map[key].completed++;
      else if (r.jo_status === "Cancelled") map[key].cancelled++;
      else if (r.jo_status === "Rescheduled") map[key].rescheduled++;
    }
    return Object.values(map);
  }, [filteredMapRecords]);

  // Top barangays by pending
  const topPendingBrgys = useMemo(() => {
    return [...filteredBrgyStats]
      .filter((b) => (b.pending || 0) > 0)
      .sort((a, b) => b.pending - a.pending)
      .slice(0, 10);
  }, [filteredBrgyStats]);

  if (!canAccessMigrationDashboard) return null;

  // Desktop only — mobile users see the existing Migration Dashboard
  if (isMobile) {
    navigate("/migration/dashboard", { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen lg:h-screen bg-gray-100 flex flex-col lg:overflow-hidden">
      <Navbar />
      {/* Module accent strip */}
      <div className={`h-1 ${MIG.strip} flex-shrink-0`} />

      <main className="flex-1 flex flex-col min-h-0 px-4 lg:px-6 pt-3 pb-2 w-full relative">
        {/* View toggle — anchored top-right for consistent positioning across Table/Map views */}
        <div
          className="absolute top-3 right-4 lg:right-6 z-20 flex rounded-lg border border-gray-300 overflow-hidden cursor-pointer"
          onClick={() => navigate("/migration/for-dispatch", { state: { dateFrom, dateTo } })}
        >
          <span className="px-3 py-1.5 text-sm font-medium bg-white text-gray-600 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" /></svg>
            Table
          </span>
          <span className={`px-3 py-1.5 text-sm font-medium ${MIG.btnPrimaryStatic} flex items-center gap-1`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
            Map
          </span>
        </div>

        {/* Toolbar — identical structure to Migration Table view (MigrationListPage for-dispatch) */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3 flex-shrink-0 md:pr-[180px]">
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            {/* Search — with map-specific autocomplete */}
            <div className="relative">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search…"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setSelectedRecord(null);
                  }}
                  className="border rounded px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => { setSearchTerm(""); setSearchResults([]); setSelectedRecord(null); }}
                    className="text-gray-400 hover:text-gray-600 text-lg"
                  >×</button>
                )}
              </div>
              {searching && (
                <div className="absolute right-3 top-2 text-xs text-gray-400">…</div>
              )}
              {searchResults.length > 0 && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded shadow-lg max-h-64 overflow-y-auto">
                  {searchResults.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => handleSelectResult(r)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 border-b last:border-b-0"
                    >
                      <span className="font-medium text-gray-800">
                        {r.oldacctno}
                      </span>
                      <span className="text-gray-500 ml-2">{r.name}</span>
                      <span className="text-gray-400 ml-2 text-xs">
                        {r.barangay}, {r.municipality}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <DateFilter value={{ from: dateFrom, to: dateTo }} onChange={handleDateChange} accent="purple" showViewAll={false} />
          </div>
          <div className="flex gap-2 items-center justify-end flex-wrap">
            {hasPerm("migration.coords_backfill") && <CoordinateBackfillTool onComplete={fetchData} />}
            <div className="inline-flex items-center gap-2 rounded border border-teal-300 bg-teal-100 px-3 py-1.5 select-none whitespace-nowrap leading-none">
              <span className="text-xs font-semibold uppercase tracking-wide text-teal-800">Teams</span>
              <span className="text-sm font-bold text-teal-900 tabular-nums">{teamsCount}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3 mb-3">
            {error}
          </div>
        )}

        {/* Stats — identical structure to Migration Table view (MigrationListPage for-dispatch) */}
        <div className="flex items-stretch gap-2 mb-3 flex-shrink-0">
          <div className={`flex-shrink-0 ${MIG.badge} rounded-lg shadow-sm flex items-center gap-2 px-4 py-2`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={MIG.iconPath} /></svg>
            <span className="text-xs font-bold uppercase tracking-wider">Migration</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1">
          {/* Pending card */}
          {(() => {
            const isPendingActive = filterStatus === "Pending" && !pendingTeamFilter;
            const isWithTeamActive = filterStatus === "Pending" && pendingTeamFilter === "with_team";
            const isWithoutTeamActive = filterStatus === "Pending" && pendingTeamFilter === "without_team";
            const pendingCount = legendCounts.Pending || 0;
            const { pendingWithTeam, pendingWithoutTeam } = mapSubStats;
            const handlePendingClick = () => {
              resetSubFilters();
              if (isPendingActive) setFilterStatus("");
              else setFilterStatus("Pending");
            };
            const handleTeamClick = (type) => (e) => {
              e.stopPropagation();
              setCompanyFilter(null); setCancelReasonFilter(null); setReschedReasonFilter(null);
              if (pendingTeamFilter === type) { setFilterStatus(""); setPendingTeamFilter(null); }
              else { setFilterStatus("Pending"); setPendingTeamFilter(type); }
            };
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-yellow-100 text-yellow-800 transition-all hover:shadow cursor-pointer ${
                  (isPendingActive || isWithTeamActive || isWithoutTeamActive) ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: pendingCount === 0 && !loading ? 0.5 : 1 }}
              >
                <div onClick={pendingCount > 0 ? handlePendingClick : undefined} title="Show only Pending dispatch records"
                  className={`cursor-pointer ${isPendingActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Pending</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : pendingCount}</span>
                  </div>
                </div>
                <div className="hidden sm:flex items-center justify-center gap-1.5 mt-0.5 border-t border-yellow-300 pt-1">
                  <button onClick={handleTeamClick("with_team")} disabled={pendingWithTeam === 0}
                    className={`text-[10px] leading-tight px-1.5 py-0.5 rounded transition-all ${
                      isWithTeamActive ? 'bg-yellow-500 text-white font-bold shadow-sm' : 'hover:bg-yellow-200 text-yellow-900'
                    } ${pendingWithTeam === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                    title="Show pending with team assigned"
                  >
                    <span className="font-semibold">{pendingWithTeam}</span> w/ Team
                  </button>
                  <span className="text-yellow-400 text-[10px]">|</span>
                  <button onClick={handleTeamClick("without_team")} disabled={pendingWithoutTeam === 0}
                    className={`text-[10px] leading-tight px-1.5 py-0.5 rounded transition-all ${
                      isWithoutTeamActive ? 'bg-yellow-500 text-white font-bold shadow-sm' : 'hover:bg-yellow-200 text-yellow-900'
                    } ${pendingWithoutTeam === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                    title="Show pending without team assigned"
                  >
                    <span className="font-semibold">{pendingWithoutTeam}</span> w/o Team
                  </button>
                </div>
              </div>
            );
          })()}
          {/* Completed card */}
          {(() => {
            const isCompActive = filterStatus === "Completed" && !companyFilter;
            const isBestActive = filterStatus === "Completed" && companyFilter === "best";
            const isA6Active = filterStatus === "Completed" && companyFilter === "a6";
            const isUnkActive = filterStatus === "Completed" && companyFilter === "unknown";
            const anyCompSelected = filterStatus === "Completed";
            const compCount = legendCounts.Completed || 0;
            const { compBest, compA6, compUnknown } = mapSubStats;
            const handleCompClick = () => {
              resetSubFilters();
              if (isCompActive) setFilterStatus("");
              else setFilterStatus("Completed");
            };
            const handleCoClick = (co) => (e) => {
              e.stopPropagation();
              setPendingTeamFilter(null); setCancelReasonFilter(null); setReschedReasonFilter(null);
              if (companyFilter === co) { setFilterStatus(""); setCompanyFilter(null); }
              else { setFilterStatus("Completed"); setCompanyFilter(co); }
            };
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-green-100 text-green-800 transition-all hover:shadow cursor-pointer ${
                  anyCompSelected ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: compCount === 0 && !loading ? 0.5 : 1 }}
              >
                <div onClick={compCount > 0 ? handleCompClick : undefined} title="Show only Completed dispatch records"
                  className={`cursor-pointer ${isCompActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Completed</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : compCount}</span>
                  </div>
                </div>
                <div className="hidden sm:flex items-center justify-center gap-1 mt-0.5 border-t border-green-300 pt-1">
                  <button onClick={handleCoClick("best")} disabled={compBest === 0}
                    className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                      isBestActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                    } ${compBest === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                    title="Completed by BEST teams"
                  >
                    <span className="font-semibold">{compBest}</span> BEST
                  </button>
                  <span className="text-green-400 text-[10px]">|</span>
                  <button onClick={handleCoClick("a6")} disabled={compA6 === 0}
                    className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                      isA6Active ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                    } ${compA6 === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                    title="Completed by A6 teams"
                  >
                    <span className="font-semibold">{compA6}</span> A6
                  </button>
                  <span className="text-green-400 text-[10px]">|</span>
                  <button onClick={handleCoClick("unknown")} disabled={compUnknown === 0}
                    className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                      isUnkActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                    } ${compUnknown === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                    title="Completed by unidentified teams"
                  >
                    <span className="font-semibold">{compUnknown}</span> N/A
                  </button>
                </div>
              </div>
            );
          })()}
          {/* Rescheduled card — only shown when selected date is today */}
          {isToday && (() => {
            const reschedCount = legendCounts.Rescheduled || 0;
            const reasonCounts = mapSubStats.reschedReasonCounts || {};
            const isMainActive = filterStatus === "Rescheduled" && !reschedReasonFilter;
            const anyActive = filterStatus === "Rescheduled";
            const handleMainClick = () => {
              resetSubFilters();
              if (isMainActive) setFilterStatus("");
              else setFilterStatus("Rescheduled");
            };
            const handleReasonClick = (reason) => (e) => {
              e.stopPropagation();
              setPendingTeamFilter(null); setCompanyFilter(null); setCancelReasonFilter(null);
              if (reschedReasonFilter === reason) { setFilterStatus(""); setReschedReasonFilter(null); }
              else { setFilterStatus("Rescheduled"); setReschedReasonFilter(reason); }
            };
            const reasonEntries = Object.entries(reasonCounts).filter(([, c]) => c > 0);
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-orange-100 text-orange-800 transition-all hover:shadow cursor-pointer ${
                  anyActive ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: reschedCount === 0 && !loading ? 0.5 : 1 }}
              >
                <div onClick={reschedCount > 0 ? handleMainClick : undefined} title="Show only Rescheduled dispatch records"
                  className={`cursor-pointer ${isMainActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Rescheduled</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : reschedCount}</span>
                  </div>
                </div>
                {reasonEntries.length > 0 && (
                  <div className="hidden sm:flex flex-wrap items-center justify-center gap-1 mt-0.5 border-t border-orange-300 pt-1">
                    {reasonEntries.map(([reason, count], i) => (
                      <span key={reason} className="flex items-center gap-0">
                        {i > 0 && <span className="text-orange-400 text-[10px] mx-0.5">|</span>}
                        <button onClick={handleReasonClick(reason)}
                          className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                            reschedReasonFilter === reason ? 'bg-orange-600 text-white font-bold shadow-sm' : 'hover:bg-orange-200 text-orange-900'
                          } cursor-pointer`}
                          title={`Show rescheduled: ${reason}`}
                        >
                          <span className="font-semibold">{count}</span> {reason}
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          {/* Cancelled card */}
          {(() => {
            const cancelCount = legendCounts.Cancelled || 0;
            const reasonCounts = mapSubStats.cancelReasonCounts || {};
            const isMainActive = filterStatus === "Cancelled" && !cancelReasonFilter;
            const anyActive = filterStatus === "Cancelled";
            const handleMainClick = () => {
              resetSubFilters();
              if (isMainActive) setFilterStatus("");
              else setFilterStatus("Cancelled");
            };
            const handleReasonClick = (reason) => (e) => {
              e.stopPropagation();
              setPendingTeamFilter(null); setCompanyFilter(null); setReschedReasonFilter(null);
              if (cancelReasonFilter === reason) { setFilterStatus(""); setCancelReasonFilter(null); }
              else { setFilterStatus("Cancelled"); setCancelReasonFilter(reason); }
            };
            const reasonEntries = Object.entries(reasonCounts).filter(([, c]) => c > 0);
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-red-100 text-red-800 transition-all hover:shadow cursor-pointer ${
                  anyActive ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: cancelCount === 0 && !loading ? 0.5 : 1 }}
              >
                <div onClick={cancelCount > 0 ? handleMainClick : undefined} title="Show only Cancelled dispatch records"
                  className={`cursor-pointer ${isMainActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Cancelled</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : cancelCount}</span>
                  </div>
                </div>
                {reasonEntries.length > 0 && (
                  <div className="hidden sm:flex flex-wrap items-center justify-center gap-1 mt-0.5 border-t border-red-300 pt-1">
                    {reasonEntries.map(([reason, count], i) => (
                      <span key={reason} className="flex items-center gap-0">
                        {i > 0 && <span className="text-red-400 text-[10px] mx-0.5">|</span>}
                        <button onClick={handleReasonClick(reason)}
                          className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                            cancelReasonFilter === reason ? 'bg-red-600 text-white font-bold shadow-sm' : 'hover:bg-red-200 text-red-900'
                          } cursor-pointer`}
                          title={`Show cancelled: ${reason}`}
                        >
                          <span className="font-semibold">{count}</span> {reason}
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          </div>
        </div>

        {/* ============================================================ */}
        {/* C. MAIN CONTENT: LEFT | CENTER MAP | RIGHT                   */}
        {/* ============================================================ */}
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_300px] gap-4 lg:flex-1 lg:min-h-0 lg:overflow-hidden">
          {/* ---------- LEFT PANEL: DEPLOYED TEAMS ---------- */}
          <div className="lg:overflow-y-auto lg:min-h-0">
            <DeployedTeamsPanel
              title="Deployed Teams"
              records={companyScopedRecords}
              teamColorMap={teamColorMap}
              statusColors={STATUS_COLORS}
              isToday={isToday}
              historicalMode={!isToday}
              selectedRecordId={selectedRecord?.id ?? null}
              onJobOrderClick={handleJobOrderClick}
              onNameClick={handleSubscriberNameClick}
              showOtherCompany={showOtherCompanyTeams}
              onToggleOtherCompany={user?.company_name ? setShowOtherCompanyTeams : undefined}
            />
          </div>

          {/* ---------- CENTER: MAP ---------- */}
          <div className="bg-white rounded-lg shadow overflow-hidden relative isolate h-[500px] lg:h-full">
            {/* Subscriber locating spinner / message overlay */}
            {(subscriberLocating || subscriberLocateMsg) && (
              <div className="absolute top-14 left-1/2 -translate-x-1/2 z-[1001] flex flex-col items-center gap-2">
                {subscriberLocating && (
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg px-4 py-2.5 text-sm font-medium text-gray-700 flex items-center gap-2 border">
                    <svg className="animate-spin w-4 h-4 text-purple-500" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg>
                    Locating subscriber…
                  </div>
                )}
                {!subscriberLocating && subscriberLocateMsg && (
                  <div className={`flex items-center gap-2 rounded-lg shadow-lg px-4 py-2.5 text-sm border ${
                    subscriberLocateMsg.type === "success" ? "bg-green-50 border-green-200 text-green-700" :
                    subscriberLocateMsg.type === "warning" ? "bg-yellow-50 border-yellow-200 text-yellow-700" :
                    "bg-red-50 border-red-200 text-red-700"
                  }`}>
                    <span className="font-medium">{subscriberLocateMsg.text}</span>
                    <button
                      onClick={() => setSubscriberLocateMsg(null)}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >✕</button>
                  </div>
                )}
              </div>
            )}
            {/* Editing location toolbar */}
            {editingLocation && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1001] bg-blue-600 text-white px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium flex items-center gap-3">
                {!editLocConfirm ? (
                  <>
                    📍 Click on the map to set new location
                    <button
                      onClick={() => setEditLocConfirm(true)}
                      disabled={!subscriberPin}
                      className="px-3 py-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed rounded text-xs font-semibold transition-colors"
                    >Save</button>
                    <button
                      onClick={() => { setEditingLocation(false); setEditLocConfirm(false); setSubscriberPin(null); }}
                      className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs transition-colors"
                    >Cancel</button>
                  </>
                ) : (
                  <>
                    <span className="text-xs">Save this as subscriber's location?</span>
                    <button
                      onClick={async () => {
                        const recId = subscriberPin?.id;
                        if (!recId || !subscriberPin) return;
                        setEditLocSaving(true);
                        try {
                          await api.put(`/migration/dispatch-dashboard/${recId}/coordinates`, {
                            latitude: subscriberPin.lat,
                            longitude: subscriberPin.lng,
                          });
                          setMapRecords((prev) =>
                            prev.map((rec) => rec.id === recId ? { ...rec, latitude: subscriberPin.lat, longitude: subscriberPin.lng, location_saved: true } : rec)
                          );
                          setSubscriberPin(null);
                          setSubscriberLocateMsg(null);
                          setEditingLocation(false);
                          setEditLocConfirm(false);
                        } catch {
                          alert("Failed to save coordinates.");
                        } finally {
                          setEditLocSaving(false);
                        }
                      }}
                      disabled={editLocSaving}
                      className="px-3 py-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 rounded text-xs font-semibold transition-colors"
                    >{editLocSaving ? "Saving…" : "Confirm"}</button>
                    <button
                      onClick={() => setEditLocConfirm(false)}
                      disabled={editLocSaving}
                      className="px-3 py-1 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded text-xs transition-colors"
                    >Back</button>
                  </>
                )}
              </div>
            )}
            {/* NAP View toggle + Measure tool (top-left) */}
            {!loading && (
              <div className="absolute top-3 left-12 z-[1000] flex items-center gap-1.5">
                {canViewNapOnMap && (migMapZoom >= 16 ? (
                  <button
                    onClick={() => setNapViewEnabled((v) => !v)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md shadow-md border text-xs font-medium transition-colors ${
                      napViewEnabled
                        ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                    title={napViewEnabled ? "Hide NAP points" : "Show NAP points on map"}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    NAP {napViewEnabled ? "ON" : "OFF"}
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md shadow-md border bg-gray-100 text-gray-400 border-gray-200 text-xs cursor-default">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    NAP (zoom in)
                  </div>
                ))}
                {/* Measure tool toggle */}
                <button
                  onClick={() => setMeasureEnabled((v) => !v)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md shadow-md border text-xs font-medium transition-colors ${
                    measureEnabled
                      ? "bg-orange-500 text-white border-orange-500 hover:bg-orange-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                  title={measureEnabled ? "Turn off measure tool" : "Measure distance on map"}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21l6-6m0 0l4-4m-4 4l-4-4m4 4l4 4M21 3l-6 6m0 0l-4 4m4-4l4 4m-4-4l-4-4" />
                  </svg>
                  {measureEnabled ? `Measure: ${measureTotal < 1000 ? measureTotal.toFixed(1) + " m" : (measureTotal / 1000).toFixed(2) + " km"}` : "Measure"}
                </button>
                {measureEnabled && (
                  <button
                    onClick={() => { setMeasureEnabled(false); setMeasureTotal(0); }}
                    className="flex items-center px-1.5 py-1.5 rounded-md shadow-md border bg-white text-gray-500 border-gray-300 hover:bg-red-50 hover:text-red-500 text-xs"
                    title="Clear measurement & turn off"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            )}
            {/* Compact status legend overlay (lower-left) — parity with NI Map */}
            {!loading && (
              <div className="absolute bottom-3 left-3 z-[1000] bg-white/95 backdrop-blur-sm rounded-md shadow-md border px-2 py-1.5 flex flex-col gap-0.5">
                {Object.entries(STATUS_COLORS)
                  .filter(([label]) => isToday || label !== "Rescheduled")
                  .map(([label, color]) => {
                    const isActive = filterStatus === label;
                    return (
                      <button
                        key={label}
                        onClick={() => { resetSubFilters(); setFilterStatus(isActive ? "" : label); }}
                        className={`flex items-center gap-1.5 text-[10.5px] rounded px-1 py-[2px] transition-colors ${
                          isActive ? "bg-gray-800 text-white font-semibold" : "hover:bg-gray-100 text-gray-700"
                        }`}
                        title={`Filter: ${label}`}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-white"
                          style={{ backgroundColor: color }}
                        />
                        <span>{label}</span>
                        <span className={`ml-auto tabular-nums text-[10px] ${isActive ? "text-gray-300" : "text-gray-400"}`}>
                          {legendCounts[label] || 0}
                        </span>
                      </button>
                    );
                  })}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                Loading map...
              </div>
            ) : (
              <MapContainer
                ref={migMapRef}
                center={SERVICE_AREA_CENTER}
                zoom={SERVICE_AREA_ZOOM}
                maxZoom={21}
                style={{ height: "100%", minHeight: 500, background: "#ffffff", cursor: editingLocation ? "crosshair" : undefined }}
                scrollWheelZoom
              >
                {showBasemap && (
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    maxZoom={21}
                  />
                )}
                {showSatellite && (
                  <TileLayer
                    attribution="&copy; Google"
                    url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                    maxZoom={21}
                  />
                )}
                {showStreetLabels && (
                  <TileLayer
                    key="road-labels"
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png"
                    pane="overlayPane"
                    maxZoom={21}
                  />
                )}
                {showLandmarks && (
                  <TileLayer
                    key="landmarks"
                    attribution="&copy; Google"
                    url="https://mt1.google.com/vt/lyrs=h&x={x}&y={y}&z={z}"
                    pane="overlayPane"
                    maxZoom={21}
                  />
                )}

                {flyTarget && <FlyTo center={flyTarget} zoom={flyZoom} />}
                {panTarget && <PanTo target={panTarget} />}
                <NapPulseMarker position={focusNapPos} />
                <ZoomWatcher onZoomChange={(z) => { setShowLandmarks(z >= 17); setShowSatellite(z >= 20); }} />

                {/* City boundary layer */}
                {showCityBoundaries && cityGeoJSON && (
                  <GeoJSON
                    key="mig-disp-city-boundaries"
                    data={cityGeoJSON}
                    interactive={false}
                    style={{
                      color: "#1e40af",
                      weight: 3,
                      fillColor: "#3b82f6",
                      fillOpacity: 0.04,
                      dashArray: "6 4",
                      interactive: false,
                    }}
                    onEachFeature={(feature, layer) => {
                      const name = feature.properties.city_name || "";
                      const shortName = name.replace(/^City of /i, "");
                      layer.bindTooltip(shortName, {
                        permanent: true,
                        direction: "center",
                        className: "mig-disp-city-label-tooltip",
                      });
                    }}
                  />
                )}

                {/* Barangay boundary layer */}
                {showBrgyBoundaries && brgyGeoJSON && (
                  <GeoJSON
                    key="mig-disp-brgy-boundaries"
                    data={brgyGeoJSON}
                    interactive={false}
                    style={{
                      color: "#6b7280",
                      weight: 1,
                      fillColor: "#d1d5db",
                      fillOpacity: 0.08,
                      interactive: false,
                    }}
                  />
                )}

                {/* Barangay name labels */}
                {showBrgyLabels && brgyGeoJSON && (
                  <BrgyLabels data={brgyGeoJSON} />
                )}

                {/* Head Office marker */}
                <Marker position={HEAD_OFFICE} icon={HEAD_OFFICE_ICON} pane="markerPane">
                  <Tooltip direction="top" offset={[0, -18]}>
                    <div className="text-xs font-bold">BEST Inc. Head Office</div>
                  </Tooltip>
                </Marker>

                {/* Dispatch team markers */}
                {filteredDispatchTeams.map((loc) => {
                  const coords = resolveCoords(loc.municipality, loc.barangay);
                  if (!coords) return null;
                  const key = `mig-disp-${loc.municipality}-${loc.barangay}-teams`;
                  const teams = loc.teams;
                  const totalJobs = teams.reduce((s, t) => s + t.count, 0);
                  const labelHtml = teams.map((t) => {
                    const c = teamColorMap[t.team_number] || "#6b7280";
                    return `<span style="background:${c};color:#fff;padding:0px 4px;border-radius:3px;font-size:8px;font-weight:700;white-space:nowrap;box-shadow:0 1px 2px rgba(0,0,0,0.25);line-height:14px;">${t.team_number}</span>`;
                  }).join(" ");

                  return (
                    <Marker
                      key={key}
                      position={coords}
                      pane="markerPane"
                      zIndexOffset={1000}
                      icon={L.divIcon({
                        className: "",
                        html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
                          ${showTeamLabels ? `<div style="display:flex;gap:2px;margin-bottom:1px;">${labelHtml}</div>` : ""}
                        </div>`,
                        iconSize: [100, 16],
                        iconAnchor: [50, 28],
                      })}
                    >
                      <Tooltip direction="top" offset={[0, -14]}>
                        <div className="text-xs space-y-1">
                          <div className="font-bold">{loc.barangay} — {totalJobs} job(s)</div>
                          {teams.map((t) => (
                            <div key={t.team_number} className="flex items-center gap-1.5">
                              <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: teamColorMap[t.team_number] || "#6b7280" }} />
                              <span className="font-medium">{t.team_number}</span>
                              <span className="text-gray-500">({t.count})</span>
                            </div>
                          ))}
                        </div>
                      </Tooltip>
                    </Marker>
                  );
                })}

                {/* Barangay pie-chart markers (when no status filter active) */}
                {!filterStatus && filteredBrgyStats.map((b) => {
                  if (b.total === 0) return null;
                  const coords = resolveCoords(b.municipality, b.barangay);
                  if (!coords) return null;
                  const size = circleRadius(b.total) * 2;
                  return (
                    <Marker
                      key={`brgy-pie-${b.municipality}-${b.barangay}`}
                      position={coords}
                      icon={buildPieIcon(b, size)}
                      pane="markerPane"
                    >
                      <Tooltip direction="top" offset={[0, -(size / 2) - 4]}>
                        <div className="text-xs">
                          <div className="font-bold">{b.barangay}</div>
                          <div className="text-gray-500">{b.municipality}</div>
                          <div className="mt-1 space-y-0.5">
                            {b.pending > 0 && <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS.Pending }} /> Pending: {b.pending}</div>}
                            {b.completed > 0 && <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS.Completed }} /> Completed: {b.completed}</div>}
                            {b.rescheduled > 0 && <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS.Rescheduled }} /> Rescheduled: {b.rescheduled}</div>}
                            {b.cancelled > 0 && <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS.Cancelled }} /> Cancelled: {b.cancelled}</div>}
                          </div>
                        </div>
                      </Tooltip>
                    </Marker>
                  );
                })}

                {/* Single-status barangay markers (when status filter is active) */}
                {filterStatus && filteredBrgyStats.map((b) => {
                  const statusKey = filterStatus.toLowerCase();
                  const val = b[statusKey] || 0;
                  if (val === 0) return null;
                  const coords = resolveCoords(b.municipality, b.barangay);
                  if (!coords) return null;
                  const r = circleRadius(val);
                  return (
                    <CircleMarker
                      key={`brgy-${b.municipality}-${b.barangay}`}
                      center={coords}
                      radius={r}
                      pane="markerPane"
                      pathOptions={{
                        fillColor: STATUS_COLORS[filterStatus],
                        fillOpacity: 0.7,
                        color: "#fff",
                        weight: 1.5,
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -r]}>
                        <div className="text-xs">
                          <div className="font-bold">{b.barangay}</div>
                          <div className="text-gray-500">{b.municipality}</div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[filterStatus] }} />
                            {filterStatus}: {val}
                          </div>
                        </div>
                      </Tooltip>
                    </CircleMarker>
                  );
                })}

                {/* Individual house markers for records with saved coordinates */}
                {filteredMapRecords.filter((r) => r.latitude && r.longitude).map((r) => {
                  const statusColor = STATUS_COLORS[r.status] || STATUS_COLORS[r.jo_status] || "#9ca3af";
                  return (
                    <Marker
                      key={`mig-house-${r.id}`}
                      position={[r.latitude, r.longitude]}
                      pane="markerPane"
                      icon={L.divIcon({
                        className: "",
                        html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));">
                            <path d="M12 3L2 12h3v8h6v-5h2v5h6v-8h3L12 3z" fill="${statusColor}" stroke="#fff" stroke-width="1.5"/>
                          </svg>
                        </div>`,
                        iconSize: [18, 18],
                        iconAnchor: [9, 18],
                      })}
                      eventHandlers={{
                        click: () => {
                          setSelectedRecord(r);
                          setSelectedNap(null);
                          setRightTab("subscriber");
                        },
                      }}
                    >
                      <Popup>
                        <div className="text-xs min-w-[160px]">
                          <div className="font-bold">{r.oldacctno}</div>
                          <div
                            className="font-semibold text-blue-600 cursor-pointer hover:underline hover:text-blue-800"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (migMapRef.current) migMapRef.current.closePopup();
                              handleLocateByAddress(r);
                            }}
                            title="Click to locate by address"
                          >
                            {r.name}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="w-2 h-2 rounded-full inline-block" style={{ background: statusColor }} />
                            <span>{r.status || r.jo_status || "Pending"}</span>
                          </div>
                          <div className="text-gray-600 mt-0.5">{r.address}</div>
                          <div className="text-gray-500">{r.barangay}, {r.municipality}</div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-gray-500">{r.team_number || "No team assigned"}</span>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}

                {/* Selected record marker */}
                {selectedRecord && (() => {
                  // Prefer the record's exact coordinates so the highlight lands
                  // on the house marker; fall back to the barangay centroid.
                  const coords =
                    selectedRecord.latitude != null && selectedRecord.longitude != null &&
                    !isNaN(selectedRecord.latitude) && !isNaN(selectedRecord.longitude)
                      ? [selectedRecord.latitude, selectedRecord.longitude]
                      : resolveCoords(selectedRecord.municipality, selectedRecord.barangay);
                  if (!coords) return null;
                  return (
                    <CircleMarker
                      center={coords}
                      radius={8}
                      pathOptions={{
                        fillColor: "#3b82f6",
                        fillOpacity: 1,
                        color: "#1e40af",
                        weight: 3,
                      }}
                    >
                      <Popup>
                        <div className="text-xs">
                          <div className="font-bold">{selectedRecord.oldacctno}</div>
                          <div>{selectedRecord.name}</div>
                          <div>{selectedRecord.barangay}, {selectedRecord.municipality}</div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })()}

                {/* FST live location markers */}
                {showFSTLocations && fstLocations.map((fst) => (
                  <Marker
                    key={`fst-loc-${fst.user_id}`}
                    position={[fst.latitude, fst.longitude]}
                    icon={buildFSTIcon(fst.is_active)}
                    pane="markerPane"
                  >
                    <Tooltip direction="top" offset={[0, -30]}>
                      <div className="text-xs space-y-0.5">
                        <div className="font-bold">{fst.display_name || fst.full_name || `User ${fst.user_id}`}</div>
                        <div className="flex items-center gap-1">
                          <span className={`inline-block w-2 h-2 rounded-full ${fst.is_active ? "bg-green-500" : "bg-gray-400"}`} />
                          <span>{fst.is_active ? "Active" : "Stale"}</span>
                        </div>
                        {fst.accuracy != null && <div className="text-gray-400">Accuracy: {Math.round(fst.accuracy)}m</div>}
                        {fst.updated_at && <div className="text-gray-400">Updated: {new Date(fst.updated_at).toLocaleTimeString()}</div>}
                      </div>
                    </Tooltip>
                  </Marker>
                ))}

                {/* Subscriber locate pin (house icon) */}
                {subscriberPin && (
                  <Marker
                    position={[subscriberPin.lat, subscriberPin.lng]}
                    icon={SUBSCRIBER_SEARCH_ICON}
                    zIndexOffset={2000}
                  >
                    <Popup>
                      <div className="text-xs space-y-0.5">
                        <div className="font-bold text-gray-800">{subscriberPin.name}</div>
                        {subscriberPin.oldacctno && <div className="text-gray-500">{subscriberPin.oldacctno}</div>}
                        <div className="text-gray-600">{subscriberPin.address}</div>
                        <div className="text-gray-500">{subscriberPin.barangay}, {subscriberPin.municipality}</div>
                      </div>
                    </Popup>
                  </Marker>
                )}

                {/* Click-to-repin handler */}
                <MapClickToPlace
                  active={editingLocation && !editLocConfirm}
                  onPlaced={(lat, lng) => {
                    setSubscriberPin((prev) => prev ? { ...prev, lat, lng } : null);
                  }}
                />

                {/* NAP Overlay — permission-gated, zoom >= 16 */}
                {canViewNapOnMap && (
                  <>
                    <MigMapZoomTracker onZoomChange={setMigMapZoom} />
                    <NapOverlay
                      enabled={napViewEnabled && migMapZoom >= 16}
                      onPointClick={(pt) => {
                        setSelectedNap(pt);
                        setSelectedRecord(null);
                        setRightTab("napDetails");
                      }}
                      selectedNapId={selectedNap?.id || null}
                    />
                  </>
                )}

                {/* Measure Tool */}
                <MeasureTool enabled={measureEnabled} onTotalChange={setMeasureTotal} />
              </MapContainer>
            )}
          </div>

          {/* ---------- RIGHT PANEL ---------- */}
          <div className="flex flex-col min-h-0 lg:h-full">
            {/* Tab headers */}
            <MapPanelTabs
              accent="purple"
              active={rightTab}
              onChange={setRightTab}
              tabs={[
                { key: "layers", label: "Layers" },
                ...(canViewNapOnMap ? [{ key: "napDetails", label: "NAP Details" }] : []),
                { key: "subscriber", label: "Subscriber" },
                { key: "noTeams", label: "No Teams" },
              ]}
            />

            {/* Tab body */}
            <div className="bg-white rounded-b-lg shadow flex-1 lg:overflow-y-auto p-4">
              {/* Tab: Layers */}
              {rightTab === "layers" && (
                <div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Map Layers</div>
                  <div className="space-y-0.5">
                    {[
                      ["Street Map", showBasemap, setShowBasemap],
                      ["Satellite", showSatellite, setShowSatellite],
                      ["Street / Road Names", showStreetLabels, setShowStreetLabels],
                      ["Landmarks", showLandmarks, setShowLandmarks],
                      ["City Boundaries", showCityBoundaries, setShowCityBoundaries],
                      ["Barangay Boundaries", showBrgyBoundaries, setShowBrgyBoundaries],
                      ["Barangay Names", showBrgyLabels, setShowBrgyLabels],
                      ["Team Labels", showTeamLabels, setShowTeamLabels],
                      ["Mobile Users", showFSTLocations, setShowFSTLocations],
                    ].map(([label, value, set]) => (
                      <label
                        key={label}
                        className="flex items-center gap-2.5 text-xs text-gray-700 cursor-pointer rounded-md px-2 py-1.5 hover:bg-purple-50/60 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => set(e.target.checked)}
                          className="w-4 h-4 rounded accent-purple-600"
                        />
                        <span className="font-medium">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab: NAP Details */}
              {rightTab === "napDetails" && canViewNapOnMap && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    NAP Details
                  </h3>
                  <NapDetailsPanel
                    selectedPoint={selectedNap}
                    onClose={() => setSelectedNap(null)}
                    onRecordUpdated={() => {
                      setNapViewEnabled(false);
                      setTimeout(() => setNapViewEnabled(true), 50);
                    }}
                    onViewInTable={(napCode) => {
                      setNapTableModalSearch(napCode);
                    }}
                    compact
                  />
                </div>
              )}

              {/* Tab: Subscriber Detail */}
              {rightTab === "subscriber" && (
                <div>
                  {selectedRecord ? (
                    <RecordDetail
                      record={selectedRecord}
                      onNapClick={async (napCode) => {
                        try {
                          const res = await api.get("/nap-locator", { params: { search: napCode, page: 1, page_size: 20 } });
                          const norm = (s) => s?.replace(/\s+/g, "").toUpperCase();
                          const match = res.data.find((n) => norm(n.nap_code) === norm(napCode));
                          if (!match) return;
                          const lat = parseFloat(match.latitude), lng = parseFloat(match.longitude);
                          if (Number.isNaN(lat) || Number.isNaN(lng)) return;
                          if (canViewNapOnMap) setNapViewEnabled(true);
                          setFocusNapPos([lat, lng]);
                          setFlyTarget([lat, lng]);
                          setFlyZoom(18);
                        } catch {
                          // silently ignore
                        }
                      }}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-center py-12 px-4 text-gray-400">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                        <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                      <p className="text-xs leading-relaxed">
                        Click a job order or map marker<br />to view subscriber details.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Municipality Summary */}
              {rightTab === "noTeams" && (
                <NoTeamsPanel
                  records={noTeamRecords}
                  statusColors={STATUS_COLORS}
                  selectedRecordId={selectedRecord?.id ?? null}
                  onCardClick={handleNoTeamCardClick}
                  onNameClick={handleSubscriberNameClick}
                  onAssignTeam={handleMigTeamClick}
                  canAssignTeam={canEditMigTeam}
                />
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Inline team assignment popover (No Teams tab) */}
      {teamMenu && (
        <TeamAssignmentPopover
          key={teamMenu.record.id}
          anchorRect={teamMenu.rect}
          record={teamMenu.record}
          assignmentDate={teamMenu.record.dispatch_date}
          apiPath="/migration"
          onSave={handleMigTeamSave}
          onClose={() => setTeamMenu(null)}
        />
      )}

      {/* NAP Table Modal (opened from "View on Table" in NAP details panel) */}
      {napTableModalSearch && (
        <NapTableModal
          key={napTableModalSearch}
          lcpCode={napTableModalSearch}
          onClose={() => setNapTableModalSearch(null)}
          onRecordUpdated={() => {
            setNapViewEnabled(false);
            setTimeout(() => setNapViewEnabled(true), 50);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RecordDetail({ record, onNapClick }) {
  const statusColor = STATUS_COLORS[record.status] || "#9ca3af";
  const initials =
    (record.name || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";

  return (
    <div className="space-y-3">
      {/* Hero */}
      <div className="rounded-xl bg-gradient-to-br from-purple-600 to-purple-500 p-3 text-white shadow-sm">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold flex-shrink-0">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold leading-tight truncate">{record.name || "—"}</div>
            <div className="text-[11px] text-purple-100 truncate">Acct #{record.oldacctno || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 mt-2 text-[11px] text-purple-50">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
            <circle cx="12" cy="11" r="3" />
          </svg>
          <span className="truncate">{[record.barangay, record.municipality].filter(Boolean).join(", ") || "—"}</span>
        </div>
      </div>

      {/* Status + team quick cards */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-2">
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Status</div>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor }} />
            <span className="text-xs font-semibold text-gray-800">{record.status || "—"}</span>
          </span>
        </div>
        <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-2">
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Team</div>
          <span className="text-xs font-semibold text-gray-800">{record.team_number || "—"}</span>
        </div>
      </div>

      <Section title="Account">
        <DetailRow label="Old Acct No." value={record.oldacctno} mono />
        <DetailRow label="Bundle Type" value={record.bundle_type} />
        <DetailRow label="Address" value={record.address} />
      </Section>

      <Section title="Schedule & Crew">
        <DetailRow label="Dispatch Date" value={record.dispatch_date} />
        <DetailRow label="Personnel 1" value={record.personnel_1_name || "—"} />
        <DetailRow label="Personnel 2" value={record.personnel_2_name || "—"} />
        <DetailRow label="Call Status" value={record.call_status} />
      </Section>

      <Section title="Location">
        <DetailRow label="Municipality" value={record.municipality} />
        <DetailRow label="Barangay" value={record.barangay} />
        <DetailRow label="Contact" value={record.contact_info} />
        <DetailRow label="Decomm" value={record.decomm} />
      </Section>

      {(record.action_result || record.remarks) && (
        <Section title="Result">
          <DetailRow label="Action Result" value={record.action_result} />
          <DetailRow label="Remarks" value={record.remarks} />
        </Section>
      )}

      {(record.nap_code || record.nap_port) && (
        <Section title="NAP">
          {record.nap_code && (
            <div className="flex items-baseline justify-between gap-3 py-1.5">
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide flex-shrink-0">NAP Code</span>
              {onNapClick ? (
                <button
                  onClick={() => onNapClick(record.nap_code)}
                  className="text-[12.5px] font-medium text-purple-600 hover:text-purple-800 hover:underline text-right break-words min-w-0 flex items-center gap-1"
                >
                  <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                    <circle cx="12" cy="11" r="3" />
                  </svg>
                  {record.nap_code}
                </button>
              ) : (
                <span className="text-[12.5px] font-medium text-gray-800 text-right break-words min-w-0">{record.nap_code}</span>
              )}
            </div>
          )}
          <DetailRow label="NAP Port" value={record.nap_port} />
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-gray-100 overflow-hidden">
      <div className="bg-gray-50 px-2.5 py-1.5 text-[9px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100">
        {title}
      </div>
      <div className="px-2.5 divide-y divide-gray-50">{children}</div>
    </div>
  );
}

function DetailRow({ label, value, bold, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide flex-shrink-0">{label}</span>
      <span className={`text-gray-800 text-right break-words min-w-0 ${bold ? "font-bold" : "font-medium"} ${mono ? "font-mono text-[11.5px]" : "text-[12.5px]"}`}>
        {value}
      </span>
    </div>
  );
}

function CollapsiblePanel({ title, defaultOpen = false, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-xs font-semibold text-gray-600 uppercase tracking-wide"
      >
        <span>{title}</span>
        <span className="flex items-center gap-1.5">
          {!open && badge != null && <span className="text-gray-400 font-normal normal-case">{badge}</span>}
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </span>
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

function HighBarangayPanel({ title, data, statusKey, resolveCoords, setFlyTarget, setFlyZoom, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const top = useMemo(() => {
    return [...data]
      .filter((b) => (b[statusKey] || 0) > 0)
      .sort((a, b) => (b[statusKey] || 0) - (a[statusKey] || 0))
      .slice(0, 5);
  }, [data, statusKey]);

  const total = useMemo(() => top.reduce((s, b) => s + (b[statusKey] || 0), 0), [top, statusKey]);

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-xs font-semibold text-gray-600 uppercase tracking-wide"
      >
        <span>{title}</span>
        <span className="flex items-center gap-1.5">
          {!open && <span className="text-gray-400 font-normal normal-case">{total}</span>}
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </span>
      </button>
      {open && (
        top.length === 0 ? (
          <p className="text-xs text-gray-400 mt-3">No data</p>
        ) : (
          <div className="space-y-1.5 mt-3">
            {top.map((b, i) => (
              <button
                key={`${b.municipality}-${b.barangay}`}
                onClick={() => {
                  const coords = resolveCoords(b.municipality, b.barangay);
                  if (coords) {
                    setFlyTarget([...coords]);
                    setFlyZoom(15);
                  }
                }}
                className="w-full flex items-center gap-2 text-xs hover:bg-gray-50 rounded px-1 py-1"
              >
                <span className="text-gray-400 w-4 text-right">{i + 1}.</span>
                <span className="text-gray-700 truncate flex-1 text-left">{b.barangay}</span>
                <span className="text-gray-400">{b.municipality}</span>
                <span className="font-medium text-gray-600 w-8 text-right">{b[statusKey]}</span>
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
}
